/**
 * Redux thunk actions for the Analytics Reports module.
 *
 * Each action follows the same lifecycle:
 *   1. fetchStart  — sets the loading state so FetchWrapper shows a skeleton
 *   2. dispatch    — sends the API response to the reducer for state mapping
 *   3. fetchSuccess / fetchError — clears the loading state or flags an error
 *
 * The exported action-type constants (e.g. GET_COMPANY_DISTRIBUTION) are also
 * used by FetchWrapper and the reducer — keep them in sync if renamed.
 */
import { Dispatch } from "redux";
import * as ReportsService from "src/services/admin/reportsService";
import { fetchStart, fetchSuccess, fetchError } from "../fetchAction";

// Namespace prefix — prevents action-type collisions with other reducers in the store.
export const ACTION_NAME: string = "ADMIN_REPORTS_";

// API 1: Company summary table, top cards, and latest month graph data
export const GET_COMPANY_DISTRIBUTION = `${ACTION_NAME}GET_COMPANY_DISTRIBUTION`;
export const getCompaniesDistribution = () => {
  return (dispatch: Dispatch) => {
    dispatch(fetchStart(GET_COMPANY_DISTRIBUTION));
    ReportsService.getCompaniesDistribution()
      .then(response => {
        dispatch({ type: GET_COMPANY_DISTRIBUTION, payload: response });
      })
      .then(() => dispatch(fetchSuccess(GET_COMPANY_DISTRIBUTION)))
      .catch(() => dispatch(fetchError(GET_COMPANY_DISTRIBUTION)));
  };
};

// API 2: Contract accounts list and their charges per company
export const GET_ACCOUNTS_CHARGES_DISTRIBUTION = `${ACTION_NAME}GET_ACCOUNTS_CHARGES_DISTRIBUTION`;
export const getAccountsChargeDistribution = () => {
  return (dispatch: Dispatch) => {
    dispatch(fetchStart(GET_ACCOUNTS_CHARGES_DISTRIBUTION));
    ReportsService.getAccountsChargeDistribution()
      .then(response => {
        dispatch({ type: GET_ACCOUNTS_CHARGES_DISTRIBUTION, payload: response });
      })
      .then(() => dispatch(fetchSuccess(GET_ACCOUNTS_CHARGES_DISTRIBUTION)))
      .catch(() => dispatch(fetchError(GET_ACCOUNTS_CHARGES_DISTRIBUTION)));
  };
};

// APIs 3–5 require startDate / endDate as YYYYMMDD strings.
// The page component (billAndPayments/index.tsx) formats dates before dispatching these.

// API 3: Payment distribution breakdown (myTNBiz vs Others), filtered by date range
export const GET_PAYMENTS_DISTRIBUTION = `${ACTION_NAME}GET_PAYMENTS_DISTRIBUTION`;
export const getPaymentsDistribution = (startDate: string, endDate: string) => {
  return (dispatch: Dispatch) => {
    dispatch(fetchStart(GET_PAYMENTS_DISTRIBUTION));
    ReportsService.getPaymentsDistribution(startDate, endDate)
      .then(response => {
        dispatch({ type: GET_PAYMENTS_DISTRIBUTION, payload: response });
      })
      .then(() => dispatch(fetchSuccess(GET_PAYMENTS_DISTRIBUTION)))
      .catch(() => dispatch(fetchError(GET_PAYMENTS_DISTRIBUTION)));
  };
};

// API 4: Monthly bills vs payments comparison, filtered by date range
export const GET_BILLS_PAYMENT_DISTRIBUTION = `${ACTION_NAME}GET_BILLS_PAYMENT_DISTRIBUTION`;
export const getBillsPaymentDistribution = (startDate: string, endDate: string) => {
  return (dispatch: Dispatch) => {
    dispatch(fetchStart(GET_BILLS_PAYMENT_DISTRIBUTION));
    ReportsService.getBillsPaymentDistribution(startDate, endDate)
      .then(response => {
        dispatch({ type: GET_BILLS_PAYMENT_DISTRIBUTION, payload: response });
      })
      .then(() => dispatch(fetchSuccess(GET_BILLS_PAYMENT_DISTRIBUTION)))
      .catch(() => dispatch(fetchError(GET_BILLS_PAYMENT_DISTRIBUTION)));
  };
};

// API 5: Payment channel distribution treemap (e.g. myTNB Portal, JomPAY, EFT), filtered by date range
export const GET_PAYMENTS_CHANNEL_DISTRIBUTION = `${ACTION_NAME}GET_PAYMENTS_CHANNEL_DISTRIBUTION`;
export const getPaymentsChannelDistribution = (startDate: string, endDate: string) => {
  return (dispatch: Dispatch) => {
    dispatch(fetchStart(GET_PAYMENTS_CHANNEL_DISTRIBUTION));
    ReportsService.getPaymentsChannelDistribution(startDate, endDate)
      .then(response => {
        dispatch({ type: GET_PAYMENTS_CHANNEL_DISTRIBUTION, payload: response });
      })
      .then(() => dispatch(fetchSuccess(GET_PAYMENTS_CHANNEL_DISTRIBUTION)))
      .catch(() => dispatch(fetchError(GET_PAYMENTS_CHANNEL_DISTRIBUTION)));
  };
};